import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchVideos } from 'src/api'
import useDropdowns from 'src/hooks/useDropdowns'
import { AddLesson } from 'src/redux/actions'
import {
  buildSampleRows,
  CONTENT_PATH_DEPTH,
  importRows,
  parseBoolean,
  parseNumber,
  parseStatus,
  parseTimeUnit,
  parseVisibility,
  requireValues,
  resolveContentPath,
  resolveOptionKey,
  validateLengths,
} from 'src/util/bulkImport'
import { downloadCsv } from 'src/util/csv'

import BulkUploadPanel from '../helperComponents/BulkUploadPanel'

export const LESSON_CSV_HEADERS = [
  'language',
  'course',
  'chapter',
  'name',
  'description',
  'difficultyLevel',
  'sortOrder',
  'status',
  'visibility',
  'hrs',
  'minutes',
  'seconds',
  'coin',
  'freeForMembers',
  'videoType',
  'videoUrl',
  'fourLVideoName',
  'videoIframe',
  'videoThumbnailUrl',
  'attachmentUrl',
  'enableRating',
  'ratingQuestion',
  'enableComment',
  'commentPlaceholder',
]

const SAMPLE_LESSONS = [
  {
    language: 'English',
    course: 'Basics of Greetings',
    chapter: 'Greetings Basics',
    name: 'Greeting Someone New',
    description: 'Practice greeting a new acquaintance politely',
    difficultyLevel: 'Beginner',
    sortOrder: '1',
    status: 'Active',
    visibility: 'Students',
    hrs: '00',
    minutes: '02',
    seconds: '30',
    coin: '10',
    freeForMembers: 'Yes',
    videoType: 'NORMAL',
    videoUrl: 'https://example.com/media/greeting_someone_new.mp4',
    fourLVideoName: '',
    videoIframe: '',
    videoThumbnailUrl: 'https://example.com/media/greeting_someone_new_thumb.jpg',
    attachmentUrl: 'https://example.com/media/greeting_worksheet.pdf',
    enableRating: 'No',
    ratingQuestion: '',
    enableComment: 'No',
    commentPlaceholder: '',
  },
  {
    language: 'English',
    course: 'Basics of Greetings',
    chapter: 'Greetings Basics',
    name: 'Saying Goodbye',
    description: 'Uses an existing 4L video, matched by its name',
    difficultyLevel: 'Beginner',
    sortOrder: '2',
    status: 'Active',
    visibility: 'Students',
    hrs: '00',
    minutes: '01',
    seconds: '45',
    coin: '10',
    freeForMembers: 'No',
    videoType: '4L',
    videoUrl: '',
    fourLVideoName: 'Everyday Goodbyes',
    videoIframe: '',
    videoThumbnailUrl: '',
    attachmentUrl: '',
    enableRating: 'Yes',
    ratingQuestion: 'How helpful was this lesson?',
    enableComment: 'No',
    commentPlaceholder: '',
  },
  {
    language: 'English',
    course: 'Numbers and Counting',
    chapter: 'Numbers 1 to 20',
    name: 'Formal Introductions',
    description: 'Inactive lesson that collects written feedback from students',
    difficultyLevel: 'Absolute Beginners',
    sortOrder: '3',
    status: 'Inactive',
    visibility: 'Students',
    hrs: '00',
    minutes: '03',
    seconds: '00',
    coin: '15',
    freeForMembers: 'No',
    videoType: 'NORMAL',
    videoUrl: 'https://example.com/media/formal_introductions.mp4',
    fourLVideoName: '',
    videoIframe: '',
    videoThumbnailUrl: 'formal_introductions_thumb.jpg',
    attachmentUrl: '',
    enableRating: 'No',
    ratingQuestion: '',
    enableComment: 'Yes',
    commentPlaceholder: 'Share your thoughts on this lesson.',
  },
  {
    language: 'English',
    course: 'Numbers and Counting',
    chapter: 'Numbers 1 to 20',
    name: 'Internal Review Draft',
    description: 'Draft lesson visible only to admins; collects both rating and comments',
    difficultyLevel: 'All levels',
    sortOrder: '4',
    status: 'Active',
    visibility: 'AdminOnly',
    hrs: '00',
    minutes: '02',
    seconds: '00',
    coin: '10',
    freeForMembers: 'No',
    videoType: 'NORMAL',
    videoUrl: 'https://example.com/media/internal_review_draft.mp4',
    fourLVideoName: '',
    videoIframe: '<iframe src="https://example.com/embed/internal_review_draft"></iframe>',
    videoThumbnailUrl: '',
    attachmentUrl: '',
    enableRating: 'Yes',
    ratingQuestion: 'Rate this lesson out of 5 stars.',
    enableComment: 'Yes',
    commentPlaceholder: 'Leave a comment for the reviewer.',
  },
]

export const SAMPLE_LESSON_CSV_ROWS = buildSampleRows(LESSON_CSV_HEADERS, SAMPLE_LESSONS)

export const downloadSampleLessonCsv = () => downloadCsv('bulk-lessons-sample.csv', SAMPLE_LESSON_CSV_ROWS)

// Same limits the Create Lesson form declares in its Yup schema.
const LESSON_LENGTH_RULES = {
  name: { min: 3, max: 100, label: 'name' },
  description: { min: 5, max: 200, label: 'description' },
}

const parseVideoType = value => {
  const normalized = value.trim().toUpperCase()
  if (!normalized || normalized === 'NORMAL') return 'NORMAL'
  if (normalized === '4L') return '4L'
  throw new Error(`videoType must be NORMAL or 4L (got "${value}")`)
}

// The single form picks a 4L video from an autocomplete; the CSV names it
// instead, and the same search endpoint resolves the name to an id. Results are
// cached per upload so repeated names cost one request.
const resolveFourLVideoId = async (name, cache) => {
  const key = name.toLowerCase()
  if (cache.has(key)) return cache.get(key)

  const response = await fetchVideos({ searchText: encodeURIComponent(name) })
  const docs = response?.docs || []
  const exact = docs.filter(doc => doc.name?.trim().toLowerCase() === key)

  if (!exact.length) {
    const suggestions = docs
      .slice(0, 3)
      .map(doc => `"${doc.name}"`)
      .join(', ')
    throw new Error(
      `No 4L video named "${name}"${suggestions ? `. Closest matches: ${suggestions}` : ''}`
    )
  }
  if (exact.length > 1) {
    throw new Error(
      `${exact.length} 4L videos are named "${name}" — rename one of them, or create this lesson from the Create Lesson form`
    )
  }

  const id = exact[0].id || exact[0]._id
  cache.set(key, id)
  return id
}

const buildLessonFeedback = row => {
  const rating = parseBoolean(row.enableRating)
  const comment = parseBoolean(row.enableComment)
  if (!rating && !comment) return null

  return {
    id: Date.now().toString(36) + Math.random().toString(36).substr(2),
    feedbackQuestion: row.ratingQuestion,
    rating,
    comment,
    feedbackPlaceholder: row.commentPlaceholder,
  }
}

// Mirrors the payload AddEditLesson posts, with media passed as direct URLs
// (or the stored name of an already-uploaded file) instead of file uploads.
export const buildLessonPayload = async (row, { dropdowns, difficultyLevels, fourLVideoCache }) => {
  requireValues(row, ['name', 'description'])
  validateLengths(row, LESSON_LENGTH_RULES)
  // Resolved first so a bad relationship fails the row before any lookup call.
  const contentPath = resolveContentPath(dropdowns, row, CONTENT_PATH_DEPTH.CHAPTER)

  const videoType = parseVideoType(row.videoType)
  let video = null
  let fourLVideo = ''

  // The form requires one video source; the same rule applies per row here.
  if (videoType === '4L') {
    if (!row.fourLVideoName) {
      throw new Error('fourLVideoName is required when videoType is 4L')
    }
    fourLVideo = await resolveFourLVideoId(row.fourLVideoName, fourLVideoCache)
  } else if (!row.videoUrl) {
    throw new Error(
      row.fourLVideoName
        ? 'videoUrl is required when videoType is NORMAL — set videoType to 4L to use fourLVideoName'
        : 'videoUrl is required when videoType is NORMAL'
    )
  } else {
    video = row.videoUrl
  }

  return {
    ...contentPath,
    name: row.name,
    description: row.description,
    difficultyLevel: resolveOptionKey(difficultyLevels, row.difficultyLevel, 'difficultyLevel', {
      fallback: 'allLevels',
    }),
    sortOrder: parseNumber(row.sortOrder, 'sortOrder', { min: 0 }),
    isActive: parseStatus(row.status),
    unPublished: parseVisibility(row.visibility),
    hrs: parseTimeUnit(row.hrs, 'hrs', 50),
    minutes: parseTimeUnit(row.minutes, 'minutes', 59),
    seconds: parseTimeUnit(row.seconds, 'seconds', 59),
    coin: parseNumber(row.coin, 'coin', { min: 0 }),
    isUnLock: parseBoolean(row.freeForMembers),
    videoType,
    video,
    fourLVideo,
    videoIframe: row.videoIframe,
    videoThumbnail: row.videoThumbnailUrl,
    // Not on the Create Lesson form — sent for payload parity only.
    exerciseVideo: '',
    file: row.attachmentUrl,
    lessonFeedback: buildLessonFeedback(row),
  }
}

const BulkUploadLesson = ({ toggleDrawer, refetch }) => {
  const dispatch = useDispatch()
  const { dropdowns } = useDropdowns()
  const difficultyLevels = useSelector(store => store.mainReducer.courseDifficultyLevel)

  const onImport = useCallback(
    rows => {
      const fourLVideoCache = new Map()
      return importRows(rows, async row => {
        const payload = await buildLessonPayload(row, {
          dropdowns,
          difficultyLevels,
          fourLVideoCache,
        })
        await dispatch(AddLesson(payload, { rethrow: true }))
      })
    },
    [dispatch, dropdowns, difficultyLevels]
  )

  return (
    <BulkUploadPanel
      entityPlural="Lessons"
      headers={LESSON_CSV_HEADERS}
      onDownloadSample={downloadSampleLessonCsv}
      onImport={onImport}
      toggleDrawer={toggleDrawer}
      refetch={refetch}
      helpText={
        'Each row names its own language, course and chapter (name or id). ' +
        'Set videoType to NORMAL with a videoUrl, or 4L with fourLVideoName (matched against the ' +
        'names of existing 4L videos). videoThumbnailUrl and attachmentUrl take a full URL or the ' +
        'stored name of an already-uploaded file. Set enableRating/enableComment to Yes to collect ' +
        'a rating and/or comments, with the wording in ratingQuestion/commentPlaceholder. ' +
        'freeForMembers=Yes unlocks the lesson for free members; visibility is Students/AdminOnly; ' +
        'status is Active/Inactive; hrs/minutes/seconds set the lesson duration.'
      }
    />
  )
}

export default BulkUploadLesson
